/**
 * Event Area Editor — placement authoring types (Masters Demo, Deliverable 1).
 *
 * These mirror the enums/columns added to `public.areas` by migrations
 * 20260616000001..04. They describe the area + placement rules a later
 * auto-placement engine reads; this deliverable only authors them.
 *
 * Terminology: the configured entity is the **area** (stable name, matching
 * area_type / area_code / areas). Rules live at the area level.
 *
 * See: docs/018-masters-demo/planemax-event-zone-editor.md (v1.2)
 */

// ---------------------------------------------------------------------------
// Enums (match the DB enum types)
// ---------------------------------------------------------------------------

/** Placement function axis — orthogonal to the structural `area_type`. */
export type AreaCategory = 'parking' | 'tiedown' | 'ramp' | 'exit' | 'fuel_depot'

/** Layout family for an area's auto-placement. */
export type PlacementPattern = 'row' | 'checkerboard' | 'double_row' | 'free_stack'

/** How successive aircraft step down a lane. */
export type PlacementProgression = 'side_by_side' | 'nose_to_tail'

/** How a Double Row's second lane relates to the first. */
export type LaneCoupling = 'tail_to_tail' | 'nested' | 'same_direction'

/** Lane sizing: per-aircraft footprint vs fixed slots. */
export type PitchMode = 'variable' | 'uniform'

/** Fill-order semantics for templated areas / cost weight for free-stack. */
export type AutoSort = 'arrival' | 'departure' | 'balanced'

/** Operator/type reservation release behavior. */
export type HoldType = 'hard' | 'soft_until_date' | 'bumpable'

/** Stay profile — short quick-turn traffic vs overnight parking. */
export type TurnoverMode = 'drop_and_go' | 'parking' | 'mixed'

/** How aircraft reach a spot in this area — self-taxi vs tug/tow. */
export type AreaParkingMode = 'self_park' | 'tow_only' | 'both'

// ---------------------------------------------------------------------------
// Display labels
// ---------------------------------------------------------------------------

export const AREA_CATEGORY_LABELS: Record<AreaCategory, string> = {
  parking: 'Parking',
  tiedown: 'Tiedown',
  ramp: 'Ramp',
  exit: 'Exit (Runway Exit)',
  fuel_depot: 'Fuel Depot',
}
export const AREA_CATEGORIES: AreaCategory[] = ['parking', 'tiedown', 'ramp', 'exit', 'fuel_depot']

export const PLACEMENT_PATTERN_LABELS: Record<PlacementPattern, string> = {
  row: 'Row',
  checkerboard: 'Checkerboard',
  double_row: 'Double Row',
  free_stack: 'Free Stack',
}
export const PLACEMENT_PATTERNS: PlacementPattern[] = [
  'row',
  'checkerboard',
  'double_row',
  'free_stack',
]

export const PLACEMENT_PROGRESSION_LABELS: Record<PlacementProgression, string> = {
  side_by_side: 'Side by Side (wingtip to wingtip)',
  nose_to_tail: 'Nose to Tail (single file)',
}
export const PLACEMENT_PROGRESSIONS: PlacementProgression[] = ['side_by_side', 'nose_to_tail']

export const LANE_COUPLING_LABELS: Record<LaneCoupling, string> = {
  tail_to_tail: 'Tail to Tail (noses out both sides)',
  nested: 'Nested (tails tuck into wing gaps)',
  same_direction: 'Same Direction (second depth row)',
}
export const LANE_COUPLINGS: LaneCoupling[] = ['tail_to_tail', 'nested', 'same_direction']

export const PITCH_MODE_LABELS: Record<PitchMode, string> = {
  variable: 'Variable (each aircraft consumes its footprint)',
  uniform: 'Uniform (fixed slots sized to the largest type)',
}
export const PITCH_MODES: PitchMode[] = ['variable', 'uniform']

export const AUTO_SORT_LABELS: Record<AutoSort, string> = {
  arrival: 'Arrival order',
  departure: 'Departure order',
  balanced: 'Balanced',
}
export const AUTO_SORTS: AutoSort[] = ['arrival', 'departure', 'balanced']

export const HOLD_TYPE_LABELS: Record<HoldType, string> = {
  hard: 'Hard (held entire event)',
  soft_until_date: 'Soft (releases after cutoff date)',
  bumpable: 'Bumpable (others bumped when party arrives)',
}
export const HOLD_TYPES: HoldType[] = ['hard', 'soft_until_date', 'bumpable']

export const TURNOVER_MODE_LABELS: Record<TurnoverMode, string> = {
  drop_and_go: 'Drop-and-Go (quick-turn / fuel)',
  parking: 'Parking (overnight)',
  mixed: 'Mixed (both)',
}
export const TURNOVER_MODES: TurnoverMode[] = ['drop_and_go', 'parking', 'mixed']

export const AREA_PARKING_MODE_LABELS: Record<AreaParkingMode, string> = {
  self_park: 'Self-park only (taxi nose-in)',
  tow_only: 'Tow only (tug to spot)',
  both: 'Both (tow large aircraft, else self-park)',
}
export const AREA_PARKING_MODES: AreaParkingMode[] = ['self_park', 'tow_only', 'both']

// ---------------------------------------------------------------------------
// JSONB shapes
// ---------------------------------------------------------------------------

/** Whether the eligibility rules are a hard gate or a soft nudge. */
export type EligibilityEnforcement = 'hard' | 'soft'

/** Size caps (any subset). Units: feet / pounds. */
export interface EligibilitySize {
  max_wingspan_ft?: number | null
  max_length_ft?: number | null
  max_tail_height_ft?: number | null
  max_weight_lbs?: number | null
}

/** Aircraft-type eligibility (categories + explicit allow/block lists). */
export interface EligibilityTypes {
  allowed_categories?: string[]
  allowed_type_ids?: string[]
  blocked_type_ids?: string[]
}

/** Client/operator eligibility — the VIP-area allow-list lives here. */
export interface EligibilityClients {
  allowed_client_ids?: string[]
  allowed_operator_ids?: string[]
  blocked_client_ids?: string[]
}

/** Optional service-based constraints. */
export interface EligibilityServices {
  required_fuel_types?: string[]
  requires_amenities?: string[]
}

/** Dwell/turnover constraint — the fine cap behind `turnover_mode`. */
export interface EligibilityDwell {
  /** Reject arrivals whose planned stay exceeds this (minutes). */
  max_minutes?: number | null
  /** When false, an overnight arrival is never placed here. */
  allow_overnight?: boolean
}

/** The smart filter authored on an area (`areas.eligibility`). */
export interface AreaEligibility {
  enforcement?: EligibilityEnforcement
  size?: EligibilitySize
  types?: EligibilityTypes
  clients?: EligibilityClients
  services?: EligibilityServices
  dwell?: EligibilityDwell
}

/** Pattern-specific layout params (`placement_config.pattern_params`). */
export interface PatternParams {
  /** Row layout. */
  layout?: 'straight' | 'diagonal'
  /** Open end(s) for retrieval. */
  access?: 'single' | 'both'
  /** Approx nest depth until silhouette packing lands (tail width ÷ wingspan). */
  tail_width_ratio?: number
  /** Tight-pack rows by footprint instead of spreading lanes evenly across depth. */
  footprint_rows?: boolean
}

/** Reserved-empty capacity for last-minute / day-of arrivals. */
export interface BufferConfig {
  mode: 'percent' | 'count'
  value: number
}

/** Operator/type reservation block release behavior. */
export interface HoldConfig {
  hold_type: HoldType
  /** Free-form target identity (e.g. { kind: 'operator', operator_id }). */
  target?: Record<string, unknown>
  /** ISO date — when a soft_until_date hold releases to the general pool. */
  until_date?: string | null
}

/** Loosely-structured placement config (`areas.placement_config`). */
export interface PlacementConfig {
  pattern_params?: PatternParams
  /** Double-Row split-area: lane B's own exit (areas.id of an exit area). */
  lane_b_exit_id?: string | null
  buffer?: BufferConfig
  hold?: HoldConfig
}

// ---------------------------------------------------------------------------
// Area placement fields shared by area Row/Insert/Update
// ---------------------------------------------------------------------------

/** The auto-placement columns added to `areas`, as a reusable mixin. */
export interface AreaPlacementFields {
  area_category: AreaCategory | null
  turnover_mode: TurnoverMode | null
  /** How aircraft reach a spot here (self-taxi vs tow). NULL = inherit planner default. */
  parking_mode: AreaParkingMode | null
  /** For parking_mode = both: wingspan (ft) at/above which the aircraft is towed. */
  tow_min_wingspan_ft: number | null
  auto_placement: boolean
  fill_priority: number | null
  pattern: PlacementPattern | null
  active_from: string | null
  active_until: string | null
  release_from: string | null
  release_until: string | null
  placement_heading: number | null
  progression: PlacementProgression | null
  lanes: number | null
  coupling: LaneCoupling | null
  pitch_mode: PitchMode | null
  safety_margin_ft: number | null
  auto_sort: AutoSort | null
  taxiway_exit_point_id: string | null
  fallback_area_id: string | null
  exit_heading: number | null
  exit_throughput: number | null
  eligibility: AreaEligibility
  placement_config: PlacementConfig
}

/** Optional (insert/update) variant of {@link AreaPlacementFields}. */
export type AreaPlacementFieldsInput = Partial<AreaPlacementFields>
